/**
 * Ingredients — the raw materials recipes consume.
 *
 * Sits beside Products on the Stock Levels screen. The columns differ from a
 * product's for two reasons: an ingredient has a UNIT (kg, l, Item) and its
 * quantities are decimals, and it has no sold-out state of its own — whether
 * a dish can be made is decided per dish, by how many portions its
 * ingredients allow.
 *
 * "Uncounted" is a real status, not a zero. An ingredient nobody has weighed
 * is skipped when a dish works out its portions, which is what lets a shop
 * track the chicken and ignore the salt. Showing it as 0 would imply the
 * opposite.
 *
 * Receiving opens an intake draft rather than writing a bare purchase event:
 * a delivery has a supplier and a cost, and the document is where those live.
 * The bare path stays for the POS, which receives at the till with no paperwork.
 *
 * See docs/feature-stock-intake-and-suppliers.md §7.2.
 */
import React, {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  METRIC_OPTIONS,
  stepFor,
  type StockItem,
} from '../../../internals/repository/StockItemRepository';

export type IngredientStatus = 'ok' | 'low' | 'out' | 'uncounted';

export interface IngredientRow {
  stockId: string;
  itemName: string;
  unit: string;
  onHand: number;
  parLevel: number | null;
  status: IngredientStatus;
  usedIn: number;
  lastUnitCost: number | null;
  lastPurchasedAt: string | null;
  updatedAt: string | null;
  isActive: boolean;
  /** Preselected as the supplier when this ingredient is received. */
  defaultSupplierId: string | null;
}

const STATUS_STYLE: Record<IngredientStatus, string> = {
  ok: 'bg-emerald-50 text-emerald-700',
  low: 'bg-amber-50 text-amber-700',
  out: 'bg-red-50 text-red-700',
  uncounted: 'bg-gray-100 text-gray-600',
};

const STATUS_LABEL: Record<IngredientStatus, string> = {
  ok: 'OK',
  low: 'Low',
  out: 'Out',
  uncounted: 'Uncounted',
};

/**
 * An ingredient's status.
 *
 * Uncounted comes first and outranks everything: without a baseline the
 * on-hand figure is not a measurement, so calling it Low or OK would be
 * making something up.
 */
export function statusOfIngredient(item: StockItem): IngredientStatus {
  const counted = item.stockBaselineAt != null || item.stockUpdatedAt != null;
  if (!counted) return 'uncounted';

  // A non-numeric cache value is NOT zero. Reading it as 0 used to make a
  // corrupt row indistinguishable from a healthy one, and both read "OK".
  const raw = Number(item.stockOnHand);
  const onHand = Number.isFinite(raw) ? raw : null;
  if (onHand === null) return 'uncounted';

  // Empty is its own state, checked BEFORE par. Most ingredients have no par
  // level, so without this an ingredient at zero fell through to "OK" — a
  // green chip for something there is none of, while the till treats <= 0 as
  // sold out. See docs/stock-test-findings.md H4.
  if (onHand <= 0) return 'out';

  const par = item.parLevel;
  if (par != null && onHand <= Number(par)) return 'low';
  return 'ok';
}

/** How many active recipes name this ingredient. */
export function dishesUsing(products: any[], stockId: string): number {
  return (products ?? []).filter(
    (p) =>
      p.stockMode === 'recipe' &&
      Array.isArray(p.recipe) &&
      p.recipe.some((l: any) => l?.stockId === stockId),
  ).length;
}

export function toIngredientRows(items: StockItem[], products: any[]): IngredientRow[] {
  return (items ?? [])
    .map((item) => ({
      stockId: item.stockId,
      itemName: item.itemName,
      unit: item.metricValue,
      onHand: Number(item.stockOnHand) || 0,
      parLevel: item.parLevel == null ? null : Number(item.parLevel),
      status: statusOfIngredient(item),
      usedIn: dishesUsing(products, item.stockId),
      lastUnitCost: item.lastUnitCost == null ? null : Number(item.lastUnitCost),
      lastPurchasedAt: item.lastPurchasedAt ?? null,
      updatedAt: item.stockUpdatedAt ?? null,
      isActive: item.isActive !== false,
      defaultSupplierId: item.defaultSupplierId ?? null,
    }))
    .sort((a, b) => a.itemName.localeCompare(b.itemName));
}

const inputCls = 'nl-input';
const selectCls = 'nl-select';

interface Props {
  shopId: string;
  ingredients: StockItem[];
  products: any[];
  busy: boolean;
  onCount: (row: IngredientRow) => void;
  onWaste: (row: IngredientRow) => void;
  onHistory: (row: IngredientRow) => void;
  onSave: (item: any) => void;
  onDeactivate: (row: IngredientRow) => void;
  /** For the Default supplier picker in the add/edit form. */
  suppliers?: {supplierId: string; name: string; isActive?: boolean}[];
}

const IngredientsTab = ({
  shopId,
  ingredients,
  products,
  busy,
  onCount,
  onWaste,
  onHistory,
  onSave,
  onDeactivate,
  suppliers = [],
}: Props) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<IngredientStatus | 'all'>('all');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const rows = useMemo(
    () => toIngredientRows(ingredients, products),
    [ingredients, products],
  );

  const visible = rows.filter((r) => {
    if (!showInactive && !r.isActive) return false;
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (search && !r.itemName.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const counts = {
    all: rows.filter((r) => r.isActive).length,
    ok: rows.filter((r) => r.isActive && r.status === 'ok').length,
    low: rows.filter((r) => r.isActive && r.status === 'low').length,
    out: rows.filter((r) => r.isActive && r.status === 'out').length,
    uncounted: rows.filter((r) => r.isActive && r.status === 'uncounted').length,
  };

  /**
   * Receiving starts a document rather than an event.
   *
   * The editor is handed the ingredient and leaves quantity and cost blank —
   * the server refuses a line with no quantity, so there is nothing to save
   * until a real number is entered.
   */
  const receive = (row: IngredientRow) =>
    navigate(`/stock/intake/new?prefill=ingredient:${encodeURIComponent(row.stockId)}`);

  const openNew = () =>
    setEditing({itemName: '', metricValue: 'kg', parLevel: '', defaultSupplierId: '', isNew: true});

  const save = () => {
    if (!editing.itemName.trim()) return;
    onSave({
      ...(editing.stockId ? {stockId: editing.stockId} : {}),
      itemName: editing.itemName.trim(),
      metricValue: editing.metricValue,
      parLevel: editing.parLevel === '' ? null : Number(editing.parLevel),
      // Prefills the supplier when this ingredient is received
      // (GRN/variants spec 5.4). Blank clears it.
      defaultSupplierId: editing.defaultSupplierId || null,
    });
    setEditing(null);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 pb-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search ingredients…"
          className="nl-input flex-1 w-auto min-w-[200px]"
        />
        <div className="flex gap-1">
          {(['all', 'out', 'low', 'ok', 'uncounted'] as (IngredientStatus | 'all')[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`h-10 rounded-[10px] border px-3 text-xs font-medium transition-colors ${
                statusFilter === s
                  ? 'border-brand bg-brand-soft text-brand'
                  : 'border-borderc text-text2 hover:bg-surface2'
              }`}
            >
              {s === 'all' ? 'All' : STATUS_LABEL[s]} ({counts[s]})
            </button>
          ))}
        </div>
        <label className="flex h-10 cursor-pointer select-none items-center gap-2 text-sm text-text2">
          <input
            type="checkbox"
            className="nl-check"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show deactivated
        </label>
        <button
          type="button"
          onClick={openNew}
          className="nl-btn nl-btn--primary"
        >
          Add ingredient
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-borderc">
        {visible.length === 0 ? (
          <div className="p-10 text-center">
            <p className="font-medium text-text1">
              {rows.length === 0 ? 'No ingredients yet' : 'Nothing matches'}
            </p>
            <p className="mt-1 text-sm text-text3">
              {rows.length === 0
                ? 'Add the raw materials your recipes consume — rice, chicken, oil.'
                : 'Try a different filter.'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 bg-surface2">
              <tr className="border-b border-borderc text-left">
                <th className="px-5 py-3 text-xs font-semibold text-text3">Ingredient</th>
                <th className="px-5 py-3 text-xs font-semibold text-text3">Unit</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-text3">On hand</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-text3">Par</th>
                <th className="px-5 py-3 text-xs font-semibold text-text3">Status</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-text3">Dishes</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-text3">Last cost</th>
                <th className="px-5 py-3 text-xs font-semibold text-text3">Last received</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-text3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr
                  key={row.stockId}
                  className={`border-b border-borderc hover:bg-surface2 ${row.isActive ? '' : 'opacity-55'}`}
                >
                  <td className="px-5 py-3 text-sm font-medium text-text1">
                    {row.itemName}
                    {!row.isActive && (
                      <span className="ml-2 rounded-full bg-surface2 px-2 py-0.5 text-[11px] text-text2">
                        Off
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-sm text-text2">{row.unit}</td>
                  <td className="px-5 py-3 text-right font-mono text-sm tabular-nums text-text1">
                    {row.status === 'uncounted' ? '—' : row.onHand}
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-sm tabular-nums text-text3">
                    {row.parLevel ?? '—'}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[row.status]}`}
                    >
                      {STATUS_LABEL[row.status]}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right text-sm tabular-nums text-text2">
                    {row.usedIn || '—'}
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-sm tabular-nums text-text3">
                    {row.lastUnitCost ?? '—'}
                  </td>
                  <td className="px-5 py-3 text-xs text-text3">
                    {row.lastPurchasedAt
                      ? new Date(row.lastPurchasedAt).toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => onCount(row)}
                        className="nl-btn nl-btn--soft nl-btn--xs"
                      >
                        Counted
                      </button>
                      <button
                        type="button"
                        onClick={() => receive(row)}
                        className="nl-btn nl-btn--secondary nl-btn--xs"
                      >
                        Received
                      </button>
                      <button
                        type="button"
                        onClick={() => onWaste(row)}
                        className="nl-btn nl-btn--secondary nl-btn--xs"
                      >
                        Waste
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onHistory(row)}
                        className="nl-btn nl-btn--secondary nl-btn--xs"
                      >
                        History
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setEditing({
                            stockId: row.stockId,
                            itemName: row.itemName,
                            metricValue: row.unit,
                            parLevel: row.parLevel ?? '',
                            defaultSupplierId: row.defaultSupplierId ?? '',
                            counted: row.status !== 'uncounted',
                          })
                        }
                        className="nl-btn nl-btn--secondary nl-btn--xs"
                      >
                        Edit
                      </button>
                      {row.isActive && (
                        <button
                          type="button"
                          onClick={() => onDeactivate(row)}
                          className="nl-btn nl-btn--secondary nl-btn--xs"
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="nl-backdrop">
          <div className="nl-modal max-w-md">
            <div className="border-b border-borderc px-5 py-4">
              <h2 className="text-base font-semibold text-text1">
                {editing.isNew ? 'Add ingredient' : `Edit ${editing.itemName}`}
              </h2>
            </div>
            <div className="flex flex-col gap-3 p-5">
              <div>
                <span className="mb-1 block text-xs font-medium text-text2">Name *</span>
                <input
                  className={inputCls}
                  autoFocus
                  value={editing.itemName}
                  onChange={(e) => setEditing({...editing, itemName: e.target.value})}
                  placeholder="Rice"
                />
              </div>
              <div>
                <span className="mb-1 block text-xs font-medium text-text2">Unit</span>
                <select
                  className={selectCls}
                  value={editing.metricValue}
                  disabled={editing.counted}
                  onChange={(e) => setEditing({...editing, metricValue: e.target.value})}
                >
                  {METRIC_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                {editing.counted && (
                  <p className="mt-1 text-[11px] text-text3">
                    The unit is fixed once an ingredient has stock history —
                    every quantity recorded is in it. Add a new ingredient to
                    use a different unit.
                  </p>
                )}
              </div>
              <div>
                <span className="mb-1 block text-xs font-medium text-text2">
                  Par level ({editing.metricValue})
                </span>
                <input
                  type="number"
                  min="0"
                  step={stepFor(editing.metricValue)}
                  className={inputCls}
                  value={editing.parLevel}
                  onChange={(e) => setEditing({...editing, parLevel: e.target.value})}
                  placeholder="Warn below this"
                />
              </div>
              <div>
                <span className="mb-1 block text-xs font-medium text-text2">Default supplier</span>
                <select
                  className={selectCls}
                  value={editing.defaultSupplierId ?? ''}
                  onChange={(e) => setEditing({...editing, defaultSupplierId: e.target.value})}
                >
                  <option value="">None</option>
                  {suppliers
                    .filter((s) => s.isActive !== false || s.supplierId === editing.defaultSupplierId)
                    .map((s) => (
                      <option key={s.supplierId} value={s.supplierId}>
                        {s.name}
                      </option>
                    ))}
                </select>
                <p className="mt-1 text-[11px] text-text3">
                  Chosen for you when this ingredient is received.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-borderc px-5 py-4">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="nl-btn nl-btn--secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                className="nl-btn nl-btn--primary"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IngredientsTab;
